export {}

interface WidgetSettings {
    WidgetID: string
    WidgetType: number
    EnergyType?: number | null
    WidgetsIDs?: string[]
}

interface MigrationResponse {
    WidgetsToAdd: WidgetSettings[]
    GroupUpdates: Record<string, string[]>
}

const MIGRATION_VERSION_KEY_PREFIX = "dashboardMigrationVersion_"
const CURRENT_MIGRATION_VERSION = 1

async function runDashboardMigration(): Promise<void> {
    const realEstateId = getRealEstateIdFromCookie()
    if (!realEstateId) {
        return
    }

    const migrationVersionKey = `${MIGRATION_VERSION_KEY_PREFIX}${realEstateId}`
    const currentVersion = parseInt(localStorage.getItem(migrationVersionKey) || "0")

    if (currentVersion >= CURRENT_MIGRATION_VERSION) {
        return
    }

    try {
        const allWidgets = getAllWidgetsFromLocalStorage(realEstateId)
        if (allWidgets.length === 0) {
            return
        }

        const response = await fetch("/Widget/MigrateDashboard", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                realEstateId: realEstateId,
                currentVersion: currentVersion,
                widgets: allWidgets.map((w) => ({
                    widgetID: w.WidgetID,
                    widgetType: w.WidgetType,
                    energyType: w.EnergyType,
                    widgetsIDs: w.WidgetsIDs,
                })),
            }),
        })

        if (!response.ok) {
            console.error(`Migration failed. HTTP status: ${response.status}`)
            return
        }

        const { WidgetsToAdd, GroupUpdates }: MigrationResponse = await response.json()

        if (!WidgetsToAdd || !GroupUpdates) {
            console.error('Invalid migration response structure')
            return
        }

        WidgetsToAdd.forEach((widget: WidgetSettings) => {
            // Double-check widget doesn't already exist
            if (!localStorage.getItem(widget.WidgetID)) {
                localStorage.setItem(widget.WidgetID, JSON.stringify(widget))
            }
        })

        Object.entries(GroupUpdates).forEach(([groupId, widgetIdsToAdd]) => {
            const groupJson = localStorage.getItem(groupId)
            if (groupJson) {
                try {
                    const group: WidgetSettings = JSON.parse(groupJson)
                    if (group.WidgetsIDs && Array.isArray(group.WidgetsIDs)) {
                        group.WidgetsIDs.push(...widgetIdsToAdd)
                        localStorage.setItem(groupId, JSON.stringify(group))
                    }
                } catch (e) {
                    console.error(`Failed to update group ${groupId}:`, e)
                }
            }
        })

        const currentRealEstateId = getRealEstateIdFromCookie()
        if (currentRealEstateId !== realEstateId) {
            console.warn("Real estate changed during migration, aborting")
            return
        }

        localStorage.setItem(migrationVersionKey, CURRENT_MIGRATION_VERSION.toString())

        if (WidgetsToAdd.length > 0 || Object.keys(GroupUpdates).length > 0) {
            window.location.reload()
        }
    } catch (error) {
        console.error("Dashboard migration failed:", error)
    }
}

function getRealEstateIdFromCookie(): number | null {
    const cookies = document.cookie.split(";")
    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split("=")
        if (name === "realEstateID") {
            return parseInt(value)
        }
    }
    return null
}

function getAllWidgetsFromLocalStorage(realEstateId: number): WidgetSettings[] {
    const widgets: WidgetSettings[] = []
    const prefix = `${realEstateId}_`

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(prefix) && !key.includes("_preview")) {
            try {
                const widgetData = JSON.parse(localStorage.getItem(key)!)
                if (widgetData && widgetData.WidgetID) {
                    widgets.push(widgetData as WidgetSettings)
                }
            } catch (e) {
                // Skip invalid JSON
            }
        }
    }

    return widgets
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => void runDashboardMigration())
} else {
    void runDashboardMigration()
}
